<template>
  <div class="material-preview">
    <div class="material-preview__content">
      <!-- Description -->
      <div class="session-content-block" v-if="material.comments">
        <h4 class="session-content-block__title">Description</h4>
        <div class="session-content-block__data" v-html="material.comments"></div>
      </div>

      <!-- VIDEO SECTION (IS_VIDEO_TYPE or IS_URL && IS_VIDEO) -->
      <div v-if="IS_VIDEO_TYPE || (IS_URL && IS_VIDEO)" class="video-preview-wrapper">
        <!-- Заглушка: відсутній src -->
        <div v-show="!videoSrc" class="video-alert-wrapper">
          <div class="video-alert warning">
            <i class="fa fa-link-off"></i>
            <p>The video source is missing or incorrect.</p>
          </div>
        </div>

        <!-- Заглушка: обробка відео -->
        <div v-show="videoSrc && isProcessing && !hasLoadError" class="video-alert-wrapper">
          <div class="video-alert info">
            <i class="fa fa-clock"></i>
            <p>The video is being prepared for playback. Please wait...</p>
          </div>
        </div>

        <!-- Заглушка: помилка завантаження -->
        <div v-show="videoSrc && hasLoadError" class="video-alert-wrapper">
          <div class="video-alert error">
            <i class="fa fa-video-slash"></i>
            <p>Failed to load the video.</p>
            <c-button size="sm" theme="primary" @click="retryVideo">Try Again</c-button>
          </div>
        </div>

        <!-- VIDEO PLAYER - завжди в DOM, але ховаємо через v-show -->
        <div v-show="videoSrc && !isProcessing && !hasLoadError" class="video-container">
          <video
            class="video-preview video-js vjs-default-skin vjs-big-play-centered"
            ref="videoPlayer"
            :id="playerId"
          ></video>
        </div>
      </div>

      <!-- URL (not video) -->
      <div v-if="IS_URL && !IS_VIDEO">
        <c-button
          v-if="material.is_zoom_recording"
          size="sm"
          theme="primary"
          icon="fa-play"
          :href="material.url"
          target="_blank"
          uppercase
          fill
        >Watch Video Recording</c-button>

        <a
          v-if="!material.is_zoom_recording"
          :href="material.url"
          target="_blank"
        >{{ material.url }}</a>
      </div>

      <!-- UPLOAD SECTION -->
      <div v-if="IS_UPLOAD" class="material-preview__upload">
        <!-- PDF -->
        <div class="file-preview" v-if="IS_PDF">
          <nav v-if="material.allow_download">
            <div class="file-preview-navigation">
              <span>File </span>
              <c-button
                size="sm"
                theme="default"
                icon="fa-download"
                @click="download(material.uuid_url, material.original_filename)"
                uppercase
                :showIndicator="showDownloadIndicator"
                fill
              >Download</c-button>
            </div>
          </nav>
          <div class="file-preview__viewer">
            <embed
              :src="`${material.uuid_url}#toolbar=${material.allow_download ? '1' : '0'}&view=FitH`"
              width="100%"
              height="100%"
              type="application/pdf"
              class="desktop-only"
            />
            <div class="mobile-only pdf-mobile">
              <PDFViewer
                :controls="['zoom']"
                :source="material.uuid_url"
                style="height: 100%; width: 100%;"
                @download="download(material.uuid_url, material.original_filename)"
              />
            </div>
          </div>
        </div>

        <!-- WORD/EXCEL/PPT -->
        <div class="file-preview" v-else-if="IS_WORLD">
          <nav v-if="material.allow_download" class="file-preview-navigation">
            <span>File </span>
            <c-button
              icon="fa-download"
              size="sm"
              theme="default"
              uppercase
              fill
              :showIndicator="showDownloadIndicator"
              @click="download(material.uuid_url, material.original_filename)"
            >Download</c-button>
          </nav>
          <div class="file-preview__viewer">
            <DocViewer :url="material.uuid_url"/>
          </div>
        </div>

        <!-- NATIVE VIDEO (non-HLS) -->
        <div class="video-preview-wrapper" v-else-if="IS_VIDEO">
          <video
            class="video-preview"
            preload="none"
            controls
            :controlsList="material.allow_download ? '' : 'nodownload'"
            :oncontextmenu="material.allow_download ? '' : 'return false;'"
          >
            <source :src="material.uuid_url" type="video/mp4" />
          </video>
        </div>

        <!-- IMAGE -->
        <img width="100%" class="image-preview" :src="material.uuid_url" v-else-if="IS_IMAGE"/>

        <!-- AUDIO -->
        <audio
          style="width: 100%;"
          controls
          :controlsList="!material.allow_download && 'nodownload'"
          :src="material.uuid_url"
          v-else-if="IS_AUDIO"
        ></audio>

        <!-- OTHER FILES -->
        <div v-else class="preview-link">
          <a
            v-if="material.allow_download"
            :href="material.uuid_url"
            target="_blank"
          >{{ material.original_filename }}</a>
          <p v-else class="preview-link__unavailable">This file is not available for preview or download.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DocViewer from "./DocViewer";
import { isOfficeMimeType } from "../../../../../../global-declare-components/helpers/officeViewer";
import { normalizeMimeType } from "../../../../../../global-declare-components/helpers/mimeType";
import PDFViewer from "pdf-viewer-vue/dist/vue2-pdf-viewer";
import videojs from "video.js";
import "video.js/dist/video-js.css";
import "videojs-hls-quality-selector/dist/videojs-hls-quality-selector.js";

const PLAYBACK_RATES = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];
// Same key as the /play recordings player so the chosen speed follows the viewer
const PLAYBACK_RATE_STORAGE_KEY = 'ce_video_playback_rate';

let globalDownloadLink = null;

if (!videojs.getComponent('DownloadButton')) {
  const Button = videojs.getComponent('Button');

  class DownloadButton extends Button {
    constructor(player, options) {
      super(player, options);
      this.controlText('Download video');
    }

    buildCSSClass() {
      return `vjs-download-button ${super.buildCSSClass()}`;
    }

    createEl() {
      const el = super.createEl('button', {
        className: 'vjs-download-button vjs-control vjs-button'
      });

      const icon = videojs.dom.createEl('span', {
        className: 'vjs-icon-placeholder',
        innerHTML: '<span aria-hidden="true">⬇</span>'
      });
      el.appendChild(icon);

      const controlText = videojs.dom.createEl('span', {
        className: 'vjs-control-text',
        textContent: 'Download'
      });
      el.appendChild(controlText);

      return el;
    }

    handleClick() {
      if (globalDownloadLink) {
        const link = document.createElement('a');
        link.href = globalDownloadLink;
        link.download = 'video.mp4';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  }

  videojs.registerComponent('DownloadButton', DownloadButton);
}

export default {
  props: ['material'],
  components: {
    DocViewer,
    PDFViewer
  },
  computed: {
    MIME_TYPE() {
      return normalizeMimeType(this.material.mime_type);
    },
    IS_QUIZ() {
      return this.material.content_type === 1;
    },
    IS_UPLOAD() {
      return this.material.content_type === 2 && !this.IS_VIDEO_TYPE;
    },
    IS_URL() {
      return this.material.content_type === 3;
    },
    IS_VIDEO_TYPE() {
      return this.material.content_type === 2
        && (this.MIME_TYPE === 'video/mp4' || this.MIME_TYPE === 'video/quicktime' || this.HAS_HLS_VIDEO);
    },
    HAS_HLS_VIDEO() {
      // Processed videos expose an HLS master playlist and rendition list even
      // when mime_type is missing (e.g. s3 uploads whose uuid was cleared).
      // Treat them as video so the player renders instead of a raw file link.
      return !!this.material.external_url
        || (Array.isArray(this.material.available_qualities) && this.material.available_qualities.length > 0);
    },
    IS_PDF() {
      return this.MIME_TYPE === 'application/pdf';
    },
    IS_WORLD() {
      return isOfficeMimeType(this.material.mime_type);
    },
    IS_AUDIO() {
      return ['audio/mpeg', 'audio/mp4', 'audio/x-m4a'].includes(this.MIME_TYPE);
    },
    IS_VIDEO() {
      return this.MIME_TYPE === 'video/quicktime' || this.MIME_TYPE === 'video/mp4';
    },
    IS_IMAGE() {
      return [
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/webp',
        'image/svg+xml',
        'image/gif'
      ].includes(this.MIME_TYPE);
    },
    videoSrc() {
      const url = this.material?.external_url || this.material?.uuid_url || this.material?.url;
      if (!url || typeof url !== 'string') return '';
      if (!/^https?:\/\//i.test(url)) return '';
      return url;
    },
    videoSourceType() {
      if (!this.videoSrc) return 'video/mp4';
      const url = this.videoSrc.toLowerCase();
      if (url.includes('.m3u8') || url.includes('playlist') || url.includes('manifest')) {
        return 'application/x-mpegURL';
      }
      return 'video/mp4';
    },
    videoDownloadLink() {
      return this.material?.download_link || this.material?.uuid_url || this.material?.url || '';
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.initVideoPlayer();
    });
  },
  data() {
    return {
      showDownloadIndicator: false,
      player: null,
      playerId: `video-player-${Math.random().toString(36).substr(2, 9)}`,
      isProcessing: false,
      hasLoadError: false,
      retryAttempts: 0,
      maxRetryAttempts: 3
    };
  },
  watch: {
    'material.id'() {
      this.destroyPlayer();
      this.$nextTick(() => {
        this.initVideoPlayer();
      });
    }
  },
  beforeDestroy() {
    this.destroyPlayer();
  },
  methods: {
    initVideoPlayer() {
      if (!this.IS_VIDEO_TYPE && !(this.IS_URL && this.IS_VIDEO)) {
        return;
      }

      if (!this.videoSrc) {
        this.hasLoadError = false;
        this.isProcessing = false;
        return;
      }

      if (this.player) {
        return;
      }

      this.$nextTick(() => {
        const videoEl = this.$refs.videoPlayer;

        if (!videoEl || this.player) {
          return;
        }

        this.isProcessing = true;
        this.hasLoadError = false;

        const options = {
          controls: true,
          autoplay: false,
          preload: 'auto',
          fluid: false,
          responsive: false,
          fill: true,
          html5: {
            vhs: {
              overrideNative: true,
              withCredentials: false
            },
            nativeVideoTracks: false,
            nativeAudioTracks: false,
            nativeTextTracks: false
          },
          playbackRates: PLAYBACK_RATES,
          controlBar: {
            pictureInPictureToggle: false
          }
        };

        try {
          this.player = videojs(videoEl, options);

          this.player.src({
            src: this.videoSrc,
            type: this.videoSourceType
          });

          this.player.ready(() => {
            this.restorePlaybackRate();

            if (this.videoSourceType === 'application/x-mpegURL') {
              try {
                if (this.player.hlsQualitySelector) {
                  this.player.hlsQualitySelector({
                    displayCurrentQuality: true,
                  });
                }
              } catch (e) {
                console.warn('HLS Quality Selector failed:', e);
              }
            }

            if (this.material.allow_download && this.videoDownloadLink) {
              this.addDownloadButton();
            }

            if (!this.material.allow_download && videoEl) {
              videoEl.addEventListener('contextmenu', (e) => {
                e.preventDefault();
                return false;
              });
            }
          });

          this.player.on('loadedmetadata', () => {
            this.isProcessing = false;
            this.hasLoadError = false;
            this.retryAttempts = 0;
          });

          this.player.on('canplay', () => {
            this.isProcessing = false;
          });

          this.player.on('ratechange', () => this.rememberPlaybackRate());

          this.player.on('error', () => {
            const error = this.player.error();
            console.error('Video error:', error);
            this.isProcessing = false;
            this.hasLoadError = true;

            if (this.retryAttempts < this.maxRetryAttempts) {
              this.retryAttempts++;
              setTimeout(() => {
                this.retryVideo();
              }, 3000);
            }
          });

        } catch (e) {
          console.error('Init error:', e);
          this.hasLoadError = true;
          this.isProcessing = false;
        }
      });
    },

    addDownloadButton() {
      if (!this.player) return;

      globalDownloadLink = this.videoDownloadLink;

      if (!this.player.controlBar.getChild('DownloadButton')) {
        const fullscreenToggle = this.player.controlBar.getChild('fullscreenToggle');
        if (fullscreenToggle) {
          const index = this.player.controlBar.children().indexOf(fullscreenToggle);
          this.player.controlBar.addChild('DownloadButton', {}, index);
        } else {
          this.player.controlBar.addChild('DownloadButton', {});
        }
      }
    },

    restorePlaybackRate() {
      let rate = 1;
      try {
        rate = Number(window.localStorage.getItem(PLAYBACK_RATE_STORAGE_KEY));
      } catch (e) {
        return;
      }

      if (!PLAYBACK_RATES.includes(rate) || rate === 1) return;

      // Default first: a media (re)load resets playbackRate to defaultPlaybackRate
      this.player.defaultPlaybackRate(rate);
      this.player.playbackRate(rate);
    },

    rememberPlaybackRate() {
      if (!this.player) return;

      const rate = this.player.playbackRate();
      if (!PLAYBACK_RATES.includes(rate)) return;

      // Keeps the speed across retryVideo() src() + load()
      this.player.defaultPlaybackRate(rate);

      try {
        window.localStorage.setItem(PLAYBACK_RATE_STORAGE_KEY, String(rate));
      } catch (e) {
        // Storage blocked (private mode) — speed still works for this video
      }
    },

    retryVideo() {
      this.hasLoadError = false;
      this.isProcessing = true;

      if (this.player) {
        this.player.src({
          src: this.videoSrc,
          type: this.videoSourceType
        });
        this.player.load();
      } else {
        this.initVideoPlayer();
      }
    },

    destroyPlayer() {
      if (this.player) {
        try {
          this.player.dispose();
        } catch (e) {
          console.error('Error disposing player:', e);
        }
        this.player = null;
      }
    },

    download(url, fileName) {
      if (this.showDownloadIndicator || !url) return;

      this.showDownloadIndicator = true;
      const name = fileName || this.fileNameFromUrl(url);

      if (typeof window.median !== 'undefined' && window.median.share?.downloadFile) {
        window.median.share.downloadFile({
          url: url,
          open: false,
          filename: name
        }).catch(err => {
          console.error('Median downloadFile error:', err);
          this.notifyDownloadFailed();
        }).finally(() => {
          this.showDownloadIndicator = false;
        });
        return;
      }

      fetch(url)
        .then(resp => {
          // Without this check an S3/CDN error page would be saved under the material's file name
          if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
          return resp.blob();
        })
        .then(blob => {
          const objectUrl = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.style.display = 'none';
          a.href = objectUrl;
          a.download = name;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          // Safari may cancel the download if the blob URL is revoked synchronously after click()
          setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);
        })
        .catch(err => {
          console.error('Download failed:', err);
          this.notifyDownloadFailed();
        })
        .finally(() => {
          this.showDownloadIndicator = false;
        });
    },

    fileNameFromUrl(url) {
      try {
        const segment = new URL(url).pathname.split('/').filter(Boolean).pop() || '';
        return segment.includes('.') ? decodeURIComponent(segment) : 'download';
      } catch (e) {
        return 'download';
      }
    },

    notifyDownloadFailed() {
      window.notifier?.error('Download failed', 'The file could not be downloaded. Please try again later.');
    }
  }
};
</script>

<style lang="scss">
.pdf-mobile {
  .pdf-viewer {
    min-width: 300px;
  }
}

.mobile-only {
  @media (min-width: 900px) {
    display: none !important;
  }
}

.desktop-only {
  @media (max-width: 900px) {
    display: none !important;
  }
}

#CenterRegion {
  display: none !important;
}

.preview-description {
  font-size: 16px;
  color: var(--attendee-text-primary-color);
  margin-bottom: var(--attendee-gap-md);
}

.preview-link {
  color: var(--attendee-brand-color);
  font-size: 18px;
}

iframe,
video {
  width: 100%;
}

.file-preview {
  width: 100%;
}

.file-preview-navigation {
  margin-bottom: var(--attendee-gap-md);
}

// Standalone fallback; inside the material modal the viewer fills the remaining height instead
.file-preview__viewer {
  height: 70vh;
}

// The material modal never grows past the viewport: header and footer stay pinned, the body scrolls.
// Anchored with :has() so every other c-modal keeps its own sizing (c-modal itself caps nothing).
.modal-content:has(> .modal-body .material-preview) {
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 20px);
  max-height: calc(100dvh - 20px);

  @media (min-width: 768px) {
    max-height: calc(100vh - 60px);
    max-height: calc(100dvh - 60px);
  }

  > .modal-header,
  > .modal-footer {
    flex: 0 0 auto;
  }

  > .modal-body {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    display: flex;
    flex-direction: column;
  }
}

// Documents get a definite height so the viewer can stretch to fill it (one scrollbar, inside the document)
.modal-content:has(> .modal-body .file-preview__viewer) {
  height: calc(100vh - 20px);
  height: calc(100dvh - 20px);

  @media (min-width: 768px) {
    height: calc(100vh - 60px);
    height: calc(100dvh - 60px);
  }

  .material-preview,
  .material-preview__content,
  .material-preview__upload,
  .file-preview {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  // Blocks above the viewer keep their natural height (flex items shrink by default and would clip)
  .session-content-block,
  .file-preview > nav,
  .file-preview-navigation {
    flex-shrink: 0;
  }

  .file-preview__viewer > .pdf-mobile {
    height: 100%;
  }

  .file-preview__viewer {
    flex: 1 1 auto;
    height: auto;
    // On short screens the body scrolls instead of squashing the document to nothing
    min-height: 320px;
  }
}

.file-preview-items {
  max-width: 800px;
  margin: 0 auto 40px auto;
  box-shadow: 0 4px 7px 2px rgb(0 0 0 / 10%);
}

.video-container {
  overflow: hidden;
}

.video-preview-wrapper {
  position: relative;
  width: 100%;
  min-height: 300px;
  background: #000;
}

.video-container {
  width: 100%;
  height: 100%;
}

.video-preview {
  width: 100%;
  height: auto;
}

.image-preview {
  height: 100%;
  object-fit: cover;
}

// Стилі для заглушок
.video-alert-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  padding: 20px;
}

.video-alert {
  max-width: 600px;
  padding: 30px;
  border-radius: 8px;
  text-align: center;
  color: #fff;

  i {
    font-size: 48px;
    margin-bottom: 20px;
    display: block;
  }

  p {
    font-size: 18px;
    margin: 0 0 20px 0;
    line-height: 1.5;
  }

  &.warning {
    background-color: #ff9800;
    border: 2px solid #f57c00;
  }

  &.info {
    background-color: #2196f3;
    border: 2px solid #1976d2;
  }

  &.error {
    background-color: #f44336;
    border: 2px solid #d32f2f;
  }
}

// Video.js стилі
.video-js {
  width: 100% !important;
  height: 450px !important;

  @media (max-width: 768px) {
    height: 300px !important;
  }
}

// Центрування великої кнопки Play
.video-js .vjs-big-play-button {
  position: absolute !important;
  left: 50% !important;
  top: 50% !important;
  transform: translate(-50%, -50%) !important;
  margin: 0 !important;
}
</style>
